import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, filter, finalize, Subject, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../services/api/auth/auth.service';
import { StorageService } from '../../services/storage/storage.service';

let apiIsRefreshing = false;
const refreshTokenApiSubject: Subject<string> = new Subject<string>();

export const UNAUTHORIZED_REFRESH_TOKEN_INTERCEPTOR: HttpInterceptorFn = (req, next) => {
    if (req.url.indexOf(environment.apiServer) === -1) {
        return next(req);
    }

    const authService = inject(AuthService);
    const storageService = inject(StorageService);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status !== 401) {
                return throwError(() => error);
            }

            if (req.url.includes('/token/refresh')) {
                // 401 when calling refresh token API.
                return throwError(() => error);
            }

            if (apiIsRefreshing === false) {
                apiIsRefreshing = true;

                return authService.refreshToken().pipe(
                    tap((response) => {
                        storageService.updateToken(response.accessToken, response.refreshToken);
                    }),
                    finalize(() => (apiIsRefreshing = false)),
                    switchMap((response) => {
                        refreshTokenApiSubject.next(response.accessToken);
                        return next(updateLatestAccessToken(req, response.accessToken));
                    }),
                );
            }

            return refreshTokenApiSubject.pipe(
                filter((accessToken) => accessToken !== ''),
                take(1),
                switchMap((accessToken) => next(updateLatestAccessToken(req, accessToken))),
            );
        }),
    );

    function updateLatestAccessToken(req: HttpRequest<unknown>, accessToken: string): HttpRequest<unknown> {
        return req.clone({
            setHeaders: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
    }
};
